import type { MaybePromise } from "@utils/config";

import type { User } from "../../models/User";
import type {
  WPAsyncPageGroupDescriptor,
  WPPageDescriptor,
  WPPageGroupDescriptor,
} from "../../models/WPPageDescriptor";
import type { WPProductConfig } from "../../models/WPProductConfig";
import type { WPTestGroup } from "../../models/WPTestGroup";
import { isPageDescriptor } from "../../utils/guards/isPageDescriptor";
import { configRefs } from "../constants/config";
import { DAYS_TO_MS, HOURS_TO_MS } from "../constants/time";
import { createCache } from "../createCache";
import { slugify } from "../slugify";

import type { ApiClientOptions } from "./typings/ApiClientOptions";
import type { ApiRequestInterceptor } from "./typings/ApiRequestInterceptor";
import { fetchRequest } from "./utils/fetchRequest";

const HARDCODED_PORTAL_ID = "7dd9f657-596f-4956-9245-644e3db4ec0c";

export function makeFetchProductNavigation(
  requestInterceptor: ApiRequestInterceptor,
  options: ApiClientOptions,
  methods: {
    fetchPracticeTests: (
      user: User,
      product: WPProductConfig,
    ) => MaybePromise<WPTestGroup[]>;
    fetchTeacherAvailableTests: (
      user: User,
      product: WPProductConfig,
    ) => MaybePromise<WPTestGroup[]>;
  },
) {
  const cache = createCache<
    Array<WPPageDescriptor | WPPageGroupDescriptor | WPAsyncPageGroupDescriptor>
  >({
    timeToLiveMs: 1 * HOURS_TO_MS,
    timeToEvictMs: 1 * DAYS_TO_MS,
    ...options,
  });

  return (user: User | undefined, product: WPProductConfig) => {
    const userType = user?.type ?? "guest";

    return cache(`${userType}:${product.id}`, async () => {
      const response = await fetchRequest<{
        pages: Array<
          (WPPageDescriptor | WPPageGroupDescriptor) & {
            hasAvailableTestsTeacher?: boolean;
            hasPracticeTests?: boolean;
          }
        >;
      }>(
        requestInterceptor(
          `${await configRefs.apis.webproever.get()}/${HARDCODED_PORTAL_ID}/products/${product.id}/Navigation/user-role/${userType}`,
        ),
      );

      // intercept links to pages including available tests for teacher and
      // practice tests, as they need to auto-inject subnavigation based on the
      // list of available levels within Avenx
      const pages = response.pages.map(
        ({ hasAvailableTestsTeacher, hasPracticeTests, ...descriptor }) => {
          if (hasAvailableTestsTeacher || hasPracticeTests) {
            return {
              id: descriptor.id,
              title: descriptor.title,
              path: descriptor.path,
              internalPath: descriptor.internalPath,

              getPages: () => {
                if (!user) {
                  return [];
                }

                const response = hasPracticeTests
                  ? methods.fetchPracticeTests(user, product)
                  : methods.fetchTeacherAvailableTests(user, product);

                if (response instanceof Promise) {
                  return response.then((levels) =>
                    levels.map((level) => ({
                      id: descriptor.id,
                      path: `${descriptor.path}${slugify(level.navn)}`,
                      internalPath: descriptor.internalPath,
                      title: level.navn,
                    })),
                  );
                }

                return response.map((level) => ({
                  id: descriptor.id,
                  path: `${descriptor.path}${slugify(level.navn)}`,
                  internalPath: descriptor.internalPath,
                  title: level.navn,
                }));
              },
            };
          }

          return descriptor;
        },
      );

      // the first item in the response is always the main page, overrule the
      // path in case SiteCore isn't aware of this
      const firstPage = pages.find(isPageDescriptor);

      if (firstPage) {
        firstPage.path = product.path;
      }

      return pages;
    });
  };
}
